import { useNavigate } from "react-router-dom";
import { Box, Container, Stack } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";

/* ─── Shared tokens (matches AddReceipts) ───────────────── */
const TOKEN = {
  primary: "#36589A",
  breadcrumbColor: "#6B7280",
  fontFamily: 'Inter, "Segoe UI", sans-serif',
};

function PageShell({
  title,
  description,
  icon,
  breadcrumbs,
  summaryChips = [],
  actions,
  topSpacing = { xs: 10, sm: 11, md: 12 },
  footerHeight = 64,
  variant = "page",
  children,
}) {
  const navigate = useNavigate();

  const defaultBreadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon style={{ width: 15, height: 15, marginRight: 4 }} />,
    },
    { label: title, path: null },
  ];
  const crumbs = breadcrumbs || defaultBreadcrumbs;

  const isList = variant === "list";
  const outerSx = isList
    ? {
        pt: topSpacing,
        pb: `${footerHeight}px`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "width 0.25s ease",
        willChange: "width",
        boxSizing: "border-box",
      }
    : {
        pt: topSpacing,
        pb: `${footerHeight}px`,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      };

  const containerSx = isList
    ? {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        maxWidth: "100% !important",
        px: { xs: 2, sm: 3, md: 4 },
        overflow: "hidden",
        minHeight: 0,
        transition: "padding 0.25s ease",
        boxSizing: "border-box",
        "@media print": { display: "none" },
      }
    : {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        maxWidth: "100% !important",
        px: { xs: 2, sm: 3, md: 4 },
        boxSizing: "border-box",
      };

  const crumbBaseStyle = {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.8rem",
    color: TOKEN.breadcrumbColor,
    fontWeight: 400,
    fontFamily: TOKEN.fontFamily,
    transition: "color 0.15s, background-color 0.15s, font-weight 0.15s",
    background: "transparent",
    border: "none",
  };

  return (
    <Box sx={outerSx}>
      <Container maxWidth={false} sx={containerSx}>
        {/* Minimal header: breadcrumb + actions */}
        <Box
          sx={{
            mb: 2,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            "@media print": { display: "none" },
          }}
        >
          {/* Breadcrumb row — matches AddReceipts style */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: TOKEN.breadcrumbColor,
              fontFamily: TOKEN.fontFamily,
            }}
          >
            {crumbs.map((item, index) => {
              const isLast = index === crumbs.length - 1;

              if (isLast) {
                return (
                  <span
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "2px 8px",
                      color: TOKEN.primary,
                      fontWeight: 600,
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </span>
                );
              }

              return (
                <span
                  key={index}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    style={crumbBaseStyle}
                    onClick={() => item.path && navigate(item.path)}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && item.path)
                        navigate(item.path);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = TOKEN.primary;
                      e.currentTarget.style.fontWeight = "700";
                      e.currentTarget.style.backgroundColor = `${TOKEN.primary}14`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = TOKEN.breadcrumbColor;
                      e.currentTarget.style.fontWeight = "400";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </span>
                  <span style={{ color: "#9CA3AF" }}>›</span>
                </span>
              );
            })}
          </div>

          {/* Actions row — pinned to the right */}
          {actions && (
            <Stack direction="row" spacing={1} alignItems="center">
              {actions}
            </Stack>
          )}
        </Box>

        {/* Page Content */}
        {children}
      </Container>
    </Box>
  );
}

export default PageShell;
